import { DateTime } from "luxon";
import type { Millis } from "./types";

export const MIN = 60_000;
export const HOUR = 60 * MIN;

/**
 * The operating day is the airport's LOCAL calendar date. Everything that asks
 * "what is today?" must go through here, never through a UTC date string.
 */
export function operatingDate(at: Millis, timezone: string): string {
  return DateTime.fromMillis(at, { zone: timezone }).toISODate()!;
}

/** The local hour by which an aircraft still on the ground has stayed the night. */
export const NIGHT_HOUR = 3;

/**
 * Remain overnight: on the ground through the night. Arriving one day and leaving the next
 * is the plain case, but an aircraft that lands at 00:15 and leaves at 05:20 sleeps on its
 * stand just the same, and so does an evening arrival delayed past midnight. Both used to
 * read as an ordinary turn on the next day's board. So a stay of three hours or more that
 * is still on the ground at 03:00 local counts too.
 */
export function remainsOvernight(arrAt: Millis, depAt: Millis, timezone: string): boolean {
  if (operatingDate(arrAt, timezone) !== operatingDate(depAt, timezone)) return true;
  if (depAt - arrAt < 3 * 3_600_000) return false;
  const night = DateTime.fromMillis(depAt, { zone: timezone }).set({ hour: NIGHT_HOUR, minute: 0, second: 0, millisecond: 0 }).toMillis();
  return arrAt <= night && depAt > night;
}

/** Local midnight at the start of `date` (YYYY-MM-DD) in the airport's timezone. */
export function localMidnight(date: string, timezone: string): Millis {
  const dt = DateTime.fromISO(date, { zone: timezone }).startOf("day");
  if (!dt.isValid) throw new Error(`Invalid operating date: ${date}`);
  return dt.toMillis();
}

/**
 * The window shown and planned for one operating day: 00:00 local to 00:00 the next day.
 * DST days are 23 or 25 hours long, which Luxon handles.
 */
export function dayWindow(date: string, timezone: string): { from: Millis; to: Millis } {
  const from = localMidnight(date, timezone);
  const to = DateTime.fromMillis(from, { zone: timezone }).plus({ days: 1 }).toMillis();
  return { from, to };
}

export function addDays(date: string, days: number): string {
  return DateTime.fromISO(date).plus({ days }).toISODate()!;
}

export function formatLocal(at: Millis, timezone: string, fmt = "HH:mm"): string {
  return DateTime.fromMillis(at, { zone: timezone }).toFormat(fmt);
}

export function parseInstant(iso: string | null | undefined): Millis | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** Parse a local wall-clock time ("2026-09-05T13:15:00" with no offset) in the airport timezone. */
export function parseLocal(iso: string, timezone: string): Millis | null {
  const dt = DateTime.fromISO(iso, { zone: timezone });
  return dt.isValid ? dt.toMillis() : null;
}

/** The local hour of the day at an airport, 0-23. Banks are local things. */
export function localHour(at: Millis, timezone: string): number {
  return DateTime.fromMillis(at).setZone(timezone).hour;
}

export function overlaps(aStart: Millis, aEnd: Millis, bStart: Millis, bEnd: Millis): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** A clock rule the host's time zone tables must already know: at `local` in `zone`, the offset is `offset` minutes. */
export interface TzRule {
  zone: string;
  /** The first operating date the rule applies to. */
  from: string;
  local: string;
  offset: number;
  /** What changed, for the person who has to update the host. */
  rule: string;
}

/**
 * Nothing here converts a time itself: every operating date, day window and local hour
 * comes from the time zone tables that ship with the host (Node's ICU on the server, the
 * browser's on the client), and a host still carrying the rules from before a change gets
 * every hour of the affected season wrong, with the flights after midnight filed under the
 * previous day. The rules listed are the ones the airport lives under; a host that fails
 * one needs its tables updated before the date the rule starts.
 */
export const TZ_RULES: readonly TzRule[] = [
  // British Columbia stopped changing its clocks on 2026-11-01 and stays on UTC-7 (tzdb 2026b).
  { zone: "America/Vancouver", from: "2026-11-01", local: "2026-12-01T12:00", offset: -420, rule: "British Columbia on permanent UTC-7 from 2026-11-01 (tzdb 2026b)" },
];

const hostOffset = (zone: string, local: string): number => DateTime.fromISO(local, { zone }).offset;

/** The rules for `timezone` that this host's time zone tables get wrong; empty when they are current. */
export function staleTzRules(timezone: string, offsetAt: (zone: string, local: string) => number = hostOffset): TzRule[] {
  return TZ_RULES.filter((r) => r.zone === timezone && offsetAt(r.zone, r.local) !== r.offset);
}
